import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import {
  formatFunnelPreciseShare,
  formatFunnelShare,
  funnelColorClassNames,
  type FunnelMomentPart,
} from "./funnelFormatting.js";

export interface FunnelMomentShareBarProps {
  momentName: string;
  /** Of the stage's interactions, those with the moment. */
  share: number;
  /** The moment's interactions, split as the bar draws them. */
  parts: readonly FunnelMomentPart[];
  /** The stage's interactions in prose, from `formatFunnelStageInteractions`. */
  stageInteractions: string;
  /** The slideover's full-width bar, without the trailing percentage. */
  isExpanded?: boolean;
}

/** The moment's share of the stage, split into its parts in the flow's order. */
export const FunnelMomentShareBar: FC<FunnelMomentShareBarProps> = (props) => {
  const {
    momentName,
    share,
    parts,
    stageInteractions,
    isExpanded = false,
  } = props;
  const interactions = parts.reduce((sum, part) => sum + part.interactions, 0);
  // Each part's share of the moment's interactions.
  const segments = parts.map((part) => ({
    ...part,
    rate: interactions > 0 ? part.interactions / interactions : 0,
  }));
  const partsDescription =
    segments.length > 1
      ? `: ${segments
          .map(
            (segment) =>
              `${formatFunnelPreciseShare(share * segment.rate)} ${segment.color === "continued" ? segment.label.toLowerCase() : segment.label}`,
          )
          .join(", ")}`
      : "";
  const description = `${formatFunnelShare(share)} of ${stageInteractions} had this moment${partsDescription}.`;

  return (
    <span
      className={twMerge(
        "flex shrink-0 items-center gap-3",
        isExpanded ? "w-full" : "w-44 sm:w-64",
      )}
      title={description}
    >
      <span
        role="img"
        aria-label={`${momentName}. ${description}`}
        className={twMerge(
          "flex flex-1 overflow-hidden rounded-full bg-border-strong/40",
          isExpanded ? "h-3" : "h-2",
        )}
      >
        <span
          className="flex h-full overflow-hidden rounded-full"
          style={{ width: `${share * 100}%` }}
        >
          {segments.map((segment) => (
            <span
              key={segment.key}
              className={twMerge(
                "h-full",
                funnelColorClassNames[segment.color].background,
              )}
              style={{ width: `${segment.rate * 100}%` }}
            />
          ))}
        </span>
      </span>
      {!isExpanded && (
        <span className="w-9 text-right text-xs text-muted-foreground tabular-nums">
          {formatFunnelShare(share)}
        </span>
      )}
    </span>
  );
};
